export enum AgentFormality {
  Formal = 'formal',
  Balanced = 'balanced',
  Casual = 'casual',
}

export enum AgentTechnicalLevel {
  Basic = 'basic',
  Intermediate = 'intermediate',
  Advanced = 'advanced',
  Expert = 'expert',
}

export const AGENT_TECHNICAL_DEPTH: readonly { label: string; value: AgentTechnicalLevel }[] = [
  { label: 'Basic', value: AgentTechnicalLevel.Basic },
  { label: 'Intermediate', value: AgentTechnicalLevel.Intermediate },
  { label: 'Advanced', value: AgentTechnicalLevel.Advanced },
  { label: 'Expert', value: AgentTechnicalLevel.Expert },
] as const;

export enum AgentResponseStyle {
  CONCISE = 'concise',
  BALANCED = 'balanced',
  DETAILED = 'detailed',
  PROFESSIONAL = 'professional',
  CONVERSATIONAL = 'conversational',
}

export interface IChangeAgentStatusPayload {
  agent_id: string;
  is_disabled: boolean;
  is_archived: boolean;
  project_key: string;
}

export interface IChangeAgentStatusResponse {
  is_success: boolean;
  item_id: string;
  detail: string;
  error: unknown;
}

export type RagConfig = {
  top_k: number;
  score_threshold: number;
  embedding_model: string;
  embedding_model_provider: string;
  chunking_strategy: string;
  enable_query_enhancement: boolean;
  enable_rerank: boolean;
  dense_weight: number;
  keyword_weight: number;
};

// azure;
export type LLMConfig = {
  provider: string;
  model_name: string;
  is_blocks_provider: boolean;
  model_id?: string | null;
  model_type?: string;
  base_prompt?: string | null;
  api_key: string;
  api_base: string;
  api_version: string;
  temperature: number;
  max_tokens: number | null;
};

export type MemoryConfig = {
  last_n_conversations: number;
  enable_summary: boolean;
  is_blocks_provider: boolean;
};

export type Guardrail = {
  enable_pii_detection: boolean;
  enable_injection_detection: boolean;
  enable_output_safety: boolean;
  enable_keyword_check: boolean;
  enable_custom_rules: boolean;

  enable_pre_validation: boolean;
  enable_post_validation: boolean;

  banned_keywords: string[];
  banned_patterns: string[];

  custom_rules: CustomRule[];

  input_risk_threshold: number;
  output_risk_threshold: number;
};

export type GuardrailOutputStrategy = 'block' | 'redact';

export interface GuardrailCatalogParam {
  type: string;
  max_item_length?: number;
}

export interface GuardrailCatalogItem {
  id: string;
  default_enabled: boolean;
  params: Record<string, GuardrailCatalogParam>;
}

export interface GuardrailConfig {
  enabled: boolean;
  keywords?: string[];
  [key: string]: unknown;
}

export type CustomRule = {
  name: string;
  pattern: string;
  severity: string;
  auto_block: boolean;
  message?: string;
  enable_pre: boolean;
  enable_post: boolean;
};

export enum WidgetType {
  CHAT = 'chat',
  CALL = 'call',
}
export type WidgetSetting = {
  widget_type: WidgetType;
  fe_style: string;
};

export type Widget = {
  name: string;
  widget_settings: WidgetSetting[];
  logo_url: string;
  logo_id: string | null;
  fe_script: string | null;
  show_agent_name: boolean;
  greeting: string;
  site_url: string | null;
  brand_color: string | null;
  allowed_domains?: string[];
  allow_visitor_tool_connect?: boolean;
  AllowVisitorToolConnect?: boolean;
  host_cookie_name?: string | null;
  HostCookieName?: string | null;
  capabilities?: string[];
  Capabilities?: string[];
  predefined_questions: string[];
  enable_predefined_questions: boolean;
};

export type AgentAttachedTool = {
  id: string;
  name: string;
  type: string;
  status?: string;
  providers?: string[];
};

export type Agent = {
  id: string;
  created_date: string;
  last_updated_date: string;
  created_by: string;
  language: string;
  last_updated_by: string;
  organization_ids: string[];
  org_id?: string;
  shared_org_ids?: string[];
  platform_managed?: boolean | null;
  tags: string[];
  name: string;
  agent_type: string | null;
  role: string | null;
  description: string;
  domain_expertise: string[];
  response_style: AgentResponseStyle;
  formality_level: AgentFormality;
  technical_level: AgentTechnicalLevel;
  enable_rag: boolean;
  rag_config: RagConfig;
  llm_config: LLMConfig;
  memory_config: MemoryConfig;
  enable_questions_suggestions: boolean;
  widget_id: string;
  widget: Widget;
  logo_url: string | null;
  logo_id: string | null;
  is_disabled: boolean;
  is_archived: boolean;
  guardrail?: Guardrail;
  enable_guardrails?: boolean;
  guardrail_output_strategy?: GuardrailOutputStrategy;
  guardrails?: Record<string, GuardrailConfig>;
  enable_tools: boolean;
  tool_ids?: string[];
  tools?: AgentAttachedTool[];
  has_knowledge?: boolean;
  has_skills?: boolean;
  has_subagents?: boolean;
  enable_skills?: boolean;
  skill_ids?: string[];
  enable_subagents?: boolean;
  subagent_ids?: string[];
  enable_knowledge_base: boolean;
  kb_ids: string[];
  folder_ids: string[];
  enable_memory: boolean;
  enable_human_handoff: boolean;
  published_date?: string;
  response_type: string;
  response_format: string | null;
  is_any_agent: boolean;
  visibility?: 'private' | 'public';
  /** True when Participants[] is non-empty (owner shared with people). */
  is_shared?: boolean;
  can_edit?: boolean;
  participants?: unknown[];
  my_share_access?: {
    is_owner?: boolean;
    permission_type?: 'viewer' | 'editor' | null;
    can_share?: boolean;
    can_edit_config?: boolean;
    can_manage_subagents?: boolean;
    can_manage_skills?: boolean;
    can_manage_tools?: boolean;
    can_manage_kb?: boolean;
  };
};

export type AgentSummary = Pick<
  Agent,
  | 'id'
  | 'name'
  | 'tags'
  | 'role'
  | 'description'
  | 'logo_url'
  | 'logo_id'
  | 'is_archived'
  | 'is_disabled'
  | 'widget_id'
  | 'visibility'
> & {
  created_by?: string;
  created_date?: string;
  last_updated_date?: string;
  is_shared?: boolean;
  can_edit?: boolean;
  org_id?: string;
  shared_org_ids?: string[];
  platform_managed?: boolean | null;
  tools?: AgentAttachedTool[];
  has_knowledge?: boolean;
  has_skills?: boolean;
  has_subagents?: boolean;
  skill_ids?: string[];
  subagent_ids?: string[];
  folder_ids?: string[];
  kb_ids?: string[];
  enable_rag?: boolean;
  enable_tools?: boolean;
  enable_skills?: boolean;
  enable_subagents?: boolean;
  my_share_access?: Agent['my_share_access'];
};

export function getAgentToolIds(agent: Pick<Agent, 'tool_ids' | 'tools'>): string[] {
  return agent.tool_ids ?? agent.tools?.map((tool) => tool.id) ?? [];
}

/** Sort keys accepted by `POST /agents/queries` (`sortBy`). */
export const AGENT_LIST_SORT_BY_OPTIONS = [
  'CreatedDate',
  'LastUpdatedDate',
  'LastConversationDate',
] as const;

export type AgentListSortBy = (typeof AGENT_LIST_SORT_BY_OPTIONS)[number];

/** Upper bound for `recentlyUsedLimit`; the backend rejects larger values. */
export const RECENTLY_USED_AGENTS_LIMIT_MAX = 100;

/** Optional activity controls for the agent list query (sent as camelCase). */
export interface IGetAgentsActivityOptions {
  /** Integer 0..100. Omitted or 0 means `recently_used_agents` comes back empty. */
  recentlyUsedLimit?: number;
  sortBy?: AgentListSortBy;
  /** Backend default is `true`. */
  sortDescending?: boolean;
}

/** Per-agent conversation activity returned by the agent list query. */
export interface AgentListActivity {
  conversationCount: number;
  /** ISO timestamp, or `null` when the agent has no conversations. */
  lastConversationDate: string | null;
}

export type AgentListItem = AgentSummary & AgentListActivity;

/** Response envelope of `POST /agents/queries`, generic over the agent shape. */
export interface IAgentListResponse<TAgent> {
  agents: (TAgent & AgentListActivity)[];
  total_count: number;
  /** Most recently used agents, capped by `recentlyUsedLimit`; empty when it is omitted/0. */
  recently_used_agents: (TAgent & AgentListActivity)[];
}

export interface IGetAgentsPayload extends IGetAgentsActivityOptions {
  limit: number;
  offset: number;
  project_key: string;
  agent_type?: string;
  name?: string;
  organization_ids?: string[];
  widget_id?: string;
  languages?: string;
  tags?: string[];
  role?: string;
  is_archived?: boolean;
  is_disabled?: boolean;
  created_by?: string[];
  platform_managed?: boolean;
  /** `true` lists only agents the caller hid. `false` excludes them. Omit to include both. */
  show_hidden_agent?: boolean;
  subagentEligible?: boolean;
  excludeAgentId?: string;
}

export type IGetAgentsResponse = IAgentListResponse<AgentSummary>;

export interface IGetAgentByIdPayload {
  id: string;
  projectKey?: string;
}

export interface IGetAgentByIdResponse {
  agent: Agent;
}

export interface ICreateAgentPayload {
  name: string;
  description: string;
  create_from: string;
  project_key: string;
  is_any_agent: boolean;
  visibility?: 'private' | 'public';
  generateAvatarWithAI?: boolean;
}

export interface ICreateAgentResponse {
  item_id?: string;
  agent?: Agent;
  is_success?: boolean;
  status_code?: number | null;
  detail?: string;
  error?: Record<string, unknown>;
  data?: unknown;
}

export interface IDeleteAgentPayload {
  id: string;
  project_key: string;
}

export interface IDeleteAgentResponse {
  success?: boolean;
  message?: string;
}

export interface IUpdateAgentPreferencesPayload {
  agentId: string;
  is_hidden: boolean;
}

export interface IUpdateAgentAIConfigurationPayload {
  name: string;
  description: string;
  llm_config: LLMConfig;
  memory_config: MemoryConfig;
  enable_questions_suggestions: boolean;
  rag_config: RagConfig;
  guardrail?: Guardrail;
  guardrail_output_strategy?: GuardrailOutputStrategy;
  guardrails?: Record<string, GuardrailConfig>;
  agent_id: string;
  project_key: string;
  enable_rag?: boolean;
  enable_knowledge_base?: boolean;
  enable_tools?: boolean;
  enable_skills?: boolean;
  enable_guardrails?: boolean;
  enable_subagents?: boolean;
  tool_ids?: string[];
  skill_ids?: string[];
  subagent_ids?: string[];
  kb_ids?: string[];
  folder_ids?: string[];
  enable_memory?: boolean;
  enable_human_handoff?: boolean;
  response_type?: string;
  response_format?: string | null;
  widget?: Widget;
}

export interface IUpdateAgentAIConfigurationResponse {
  is_success: boolean;
  item_id: string;
  detail: string;
  error: unknown;
}

export interface IUpdateAgentPersonaPayload {
  agent_id: string;
  agent_type: string;
  name: string;
  language?: string;
  tags: string[];
  domain_expertise: string[];
  role: string;
  description: string;
  logo_url: string;
  logo_id: string;
  response_style: AgentResponseStyle;
  formality_level: AgentFormality;
  technical_level: AgentTechnicalLevel;
  project_key: string;
  is_any_agent?: boolean;
  visibility?: 'private' | 'public';
}

export interface IUpdateAgentPersonaResponse {
  is_success: boolean;
  item_id: string;
  detail: string;
  error: unknown;
}

export interface IUpdateAgentWidgetPayload {
  agent_id: string;
  regen_widget_id: boolean;
  project_key: string;
  widget?: Widget;
}

export interface IUpdateAgentWidgetResponse {
  is_success: boolean;
  item_id: string;
  detail: string;
  error: unknown;
  widget_id?: string;
  // Saved, but the widget cannot serve visitors until billing is configured.
  data?: { usage_credential_problem?: string } | null;
}

export interface IQueryAgentPayload {
  agent_id: string;
  project_key: string;
  query: string;
  stream?: boolean;
  kb_ids?: string[];
}

export interface IQueryAgentResponse {
  is_success: boolean;
  detail: string;
  query: string;
  response: string;
  processing_time: number;
  metadata: {
    additionalProp1: Record<string, unknown>;
  };
  sources: string;
  guardrail_violations: string;
}

export interface ILMTQueryAgentPayload {
  project_key: string;
  query: string;
  session_id: string | null;
}

export interface ILMTQueryAgentResponse extends IQueryAgentResponse {
  session_id: string;
}

export interface ISSEQueryAgentPayload {
  query: string;
  session_id?: string;
  base_prompt?: string;
  model_id?: string;
  model_name?: string;
  model_provider?: string;
  tool_ids?: string[];
  last_n_turns: number;
  enable_summary: boolean;
  enable_tools: boolean;
  enable_next_suggestion: boolean;
  response_type?: string;
  response_style?: string;
  call_from?: string;
  files?: string[];
  images?: string[];
}

export interface IProcessFilesPayload {
  session_id: string;
  call_from: string;
  file_ids: string[];
}

export interface IProcessFilesResponse {
  success: boolean;
  message?: string;
  data?: string;
}

export interface IAgentChatFileMetadata {
  file_id: string;
  extension?: string;
}

export interface IAgentChatStreamPayload {
  message: string;
  message_type: string;
  files?: IAgentChatFileMetadata[];
}

export type IWidgetColorSuggestionResponse = {
  'chat-header--background-start-color': string;
  'chat-header--background-end-color': string;
  'chat-header--title-color': string;
  'chat-window--background-color': string;
  'chat-window--font-color': string;
  'send-button--background-color': string;
  'send-button--font-color': string;
  'chat--border-color': string;
  'chat-toggle-button--background-color': string;
  'chat-toggle-button--color': string;
};

export interface IWidgetColorSuggestionPayload {
  url: string;
  brand_color: string;
  project_key: string;
}

export interface IAgentRetrievalTestingPayload {
  enable_query_enhancement: boolean;
  enable_rerank: boolean;
  ground_truth_texts: string[];
  project_key: string;
  query: string;
  score_threshold: number;
  top_k: number;
  dense_weight?: number;
  keyword_weight?: number;
}

export interface IRetrievalDocument {
  keywords: string[];
  chars: number;
  tokens: number;
  score: number;
  text: string;
}

export interface IRetrievalMetrics {
  f1_score: number;
  hit_rate: boolean;
  k: number;
  precision: number;
  recall: number;
}

export interface IRetrievalQueryResult {
  query: string;
  query_type: string;
  documents: IRetrievalDocument[];
  metrics: IRetrievalMetrics;
}

export interface IRetrievalAnalysisData {
  enhanced_queries: string[];
  score_of_adequate_context: number;
}

export interface IAgentRetrievalTestingResponse {
  is_success: boolean;
  result: IRetrievalQueryResult[];
  analysis_data: IRetrievalAnalysisData;
}

export interface IPublishAgentPayload {
  agent_id: string;
  project_key: string;
}

export interface IPublishAgentResponse {
  is_success: boolean;
  detail?: string;
  error?: unknown;
}
